import { useEffect, useRef, useState } from "react";
import { apiCall } from "../lib/api";

export type ActionsPanel = "clubMember" | "scansLink" | "downloadUrl" | "bugReport" | "featureRequest";

const NAV: { id: ActionsPanel; label: string }[] = [
  { id: "clubMember", label: "Manual Club Member" },
  { id: "scansLink", label: "Manual Scans Link" },
  { id: "downloadUrl", label: "Direct Download URL" },
  { id: "bugReport", label: "Bug Report" },
  { id: "featureRequest", label: "Feature Request" },
];

type Result = { kind: "success" | "error"; node: React.ReactNode } | null;

function CopyLink({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="copy-link">
      <span style={{ wordBreak: "break-all" }}>{value}</span>
      <button
        type="button"
        className={`copy-btn${copied ? " copied" : ""}`}
        onClick={() => {
          navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          });
        }}
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
}

export function FunctionsModal({
  open,
  panel,
  onClose,
  onPanelChange,
}: {
  open: boolean;
  panel: ActionsPanel;
  onClose: () => void;
  onPanelChange: (p: ActionsPanel) => void;
}) {
  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <div
      className={`functions-modal${open ? " show" : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="functions-modal-content">
        <div className="functions-sidebar">
          <div className="functions-sidebar-header">Actions</div>
          {NAV.map((n) => (
            <div
              key={n.id}
              className={`functions-nav-item${panel === n.id ? " active" : ""}`}
              onClick={() => onPanelChange(n.id)}
            >
              {n.label}
            </div>
          ))}
        </div>
        <div className="functions-main">
          <span className="close" onClick={onClose}>
            &times;
          </span>
          {panel === "clubMember" && <ClubMemberPanel onCancel={onClose} />}
          {panel === "scansLink" && <ScansLinkPanel onCancel={onClose} />}
          {panel === "downloadUrl" && <DownloadUrlPanel onCancel={onClose} />}
          {panel === "bugReport" && <FeedbackPanel type="bug" onCancel={onClose} />}
          {panel === "featureRequest" && <FeedbackPanel type="fr" onCancel={onClose} />}
        </div>
      </div>
    </div>
  );
}

function ResultBox({ result }: { result: Result }) {
  if (!result) return <div className="function-result" />;
  return <div className={`function-result ${result.kind}`}>{result.node}</div>;
}

function ClubMemberPanel({ onCancel }: { onCancel: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState<Result>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setResult({ kind: "error", node: "Please fill in both name and email." });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const data = await apiCall<any>("manual-onboard", {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), email: email.trim(), sendEmail }),
      });
      if (data.success) {
        setResult({
          kind: "success",
          node: (
            <>
              <strong>
                <i className="ph ph-check-circle" /> Account created successfully!
              </strong>
              <div className="step-list">
                <div><strong>Email:</strong> {data.email}</div>
                <div><strong>Customer Path:</strong> {data.customerPath}</div>
                <div><strong>User ID:</strong> {data.userId || "N/A"}</div>
                {Object.entries(data.steps || {}).map(([step, status]) => (
                  <div key={step}>{step}: {String(status)}</div>
                ))}
              </div>
            </>
          ),
        });
        setDone(true);
        setName("");
        setEmail("");
      } else {
        setResult({
          kind: "error",
          node: data.existingUser ? (
            <>
              <strong><i className="ph ph-warning" /> User already exists</strong>
              <div className="step-list">
                <div><strong>User ID:</strong> {data.existingUser.id}</div>
                <div><strong>Customer Path:</strong> {data.existingUser.customerPath}</div>
              </div>
            </>
          ) : (
            <><strong><i className="ph ph-x-circle" /> Error:</strong> {data.error || "Unknown error"}</>
          ),
        });
      }
    } catch (err) {
      setResult({ kind: "error", node: <><strong><i className="ph ph-x-circle" /> Error:</strong> {(err as Error).message}</> });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="functions-panel active">
      <h3>Manual Club Member</h3>
      <p className="panel-description">
        Create a new club member account with R2 directory, database records, and optional welcome email.
      </p>
      <form onSubmit={submit}>
        <div className="form-group">
          <label>Full Name</label>
          <input type="text" placeholder="John Doe" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input type="email" placeholder="john@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
            <span>Send welcome email with password setup link</span>
          </label>
        </div>
        <ResultBox result={result} />
        <div className="form-actions">
          <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="submit-btn" disabled={busy}>
            {busy ? "Creating..." : done ? "Done!" : "Create Account"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ScansLinkPanel({ onCancel }: { onCancel: () => void }) {
  const [email, setEmail] = useState("");
  const [path, setPath] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState<Result>(null);
  const [suggestions, setSuggestions] = useState<{ email?: string; name: string; lastDate?: string }[]>([]);
  const [showTA, setShowTA] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function onEmailChange(v: string) {
    setEmail(v);
    if (v.trim().length < 2) {
      setShowTA(false);
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const data = await apiCall<{ suggestions: typeof suggestions }>(`customer-email-typeahead?q=${encodeURIComponent(v.trim())}`);
        setSuggestions(data.suggestions || []);
        setShowTA((data.suggestions || []).length > 0);
      } catch {
        /* ignore */
      }
    }, 200);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !path.trim()) {
      setResult({ kind: "error", node: "Please fill in both email and path." });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const data = await apiCall<any>("write-scan-access", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), path: path.trim() }),
      });
      if (data.success) {
        setResult({
          kind: "success",
          node: (
            <>
              <strong><i className="ph ph-check-circle" /> Scan access created!</strong>
              <div className="step-list">
                <div><strong>KV Key:</strong> {data.kvKey}</div>
                <div><strong>Email:</strong> {data.email}</div>
              </div>
              <CopyLink value={data.downloadLink} />
            </>
          ),
        });
        setDone(true);
        setPath("");
      } else {
        setResult({ kind: "error", node: <><strong><i className="ph ph-x-circle" /> Error:</strong> {data.error || "Unknown error"}</> });
      }
    } catch (err) {
      setResult({ kind: "error", node: <><strong><i className="ph ph-x-circle" /> Error:</strong> {(err as Error).message}</> });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="functions-panel active">
      <h3>Manual Scans Link</h3>
      <p className="panel-description">
        Create a download link for customer scans by writing access to the SCAN_ACCESS KV store.
      </p>
      <form onSubmit={submit}>
        <div className="form-group">
          <label>Customer Email</label>
          <div className="typeahead-wrapper">
            <input
              type="text"
              placeholder="Search by name or email..."
              autoComplete="off"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              onBlur={() => setTimeout(() => setShowTA(false), 200)}
              required
            />
            <div className={`function-typeahead${showTA ? " show" : ""}`}>
              {suggestions.map((s, i) => (
                <div
                  key={i}
                  className="function-typeahead-item"
                  onClick={() => {
                    setEmail(s.email || s.name);
                    setShowTA(false);
                  }}
                >
                  <div className="name">{s.name}</div>
                  {s.email && <div className="email">{s.email}</div>}
                  {s.lastDate && <div className="date">Last order: {s.lastDate}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label>Scan Path</label>
          <input type="text" placeholder="03.08.26.mcveety" value={path} onChange={(e) => setPath(e.target.value)} required />
          <span className="input-hint">Enter with or without .zip extension</span>
        </div>
        <ResultBox result={result} />
        <div className="form-actions">
          <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="submit-btn" disabled={busy}>
            {busy ? "Creating..." : done ? "Done!" : "Create Link"}
          </button>
        </div>
      </form>
    </div>
  );
}

function DownloadUrlPanel({ onCancel }: { onCancel: () => void }) {
  const [path, setPath] = useState("");
  const [expires, setExpires] = useState("24");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState<Result>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!path.trim()) {
      setResult({ kind: "error", node: "Please enter a file path." });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const data = await apiCall<any>("generate-download-url", {
        method: "POST",
        body: JSON.stringify({ path: path.trim(), expiresIn: (parseInt(expires, 10) || 24) * 3600 }),
      });
      if (data.success) {
        const sizeMB = (data.file.size / 1024 / 1024).toFixed(1);
        setResult({
          kind: "success",
          node: (
            <>
              <strong>✅ Presigned URL ready</strong>
              <div className="step-list">
                <div><strong>Key:</strong> {data.file.key}</div>
                <div><strong>Size:</strong> {sizeMB} MB</div>
                <div><strong>Expires:</strong> {new Date(data.expiresAt).toLocaleString()}</div>
              </div>
              <CopyLink value={data.url} />
            </>
          ),
        });
        setDone(true);
      } else {
        setResult({ kind: "error", node: <><strong><i className="ph ph-x-circle" /> Error:</strong> {data.error || "Unknown error"}</> });
      }
    } catch (err) {
      setResult({ kind: "error", node: <><strong><i className="ph ph-x-circle" /> Error:</strong> {(err as Error).message}</> });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="functions-panel active">
      <h3>Direct Download URL</h3>
      <p className="panel-description">
        Generate a short-lived presigned R2 URL the customer can use to download directly (resumable, no email check, no
        KV write). Use this for one-off issues — for normal delivery use Manual Scans Link.
      </p>
      <form onSubmit={submit}>
        <div className="form-group">
          <label>File Path</label>
          <input type="text" placeholder="05.11.26.feign or scans/05.11.26.feign.zip" value={path} onChange={(e) => setPath(e.target.value)} required />
          <span className="input-hint">Filename, scan path (auto-prefixed with scans/ + .zip), or full R2 key</span>
        </div>
        <div className="form-group">
          <label>Expires In (hours)</label>
          <input type="number" min={1} max={168} value={expires} onChange={(e) => setExpires(e.target.value)} />
          <span className="input-hint">Max 168 (7 days)</span>
        </div>
        <ResultBox result={result} />
        <div className="form-actions">
          <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="submit-btn" disabled={busy}>
            {busy ? "Generating..." : done ? "Done!" : "Generate URL"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FeedbackPanel({ type, onCancel }: { type: "bug" | "fr"; onCancel: () => void }) {
  const [content, setContent] = useState("");
  const [screenshot, setScreenshot] = useState<File | Blob | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const title = type === "bug" ? "Bug Report" : "Feature Request";
  const submitLabel = type === "bug" ? "Submit Bug Report" : "Submit Feature Request";

  function setFile(f: File | Blob) {
    setScreenshot(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(String(e.target?.result || ""));
    reader.readAsDataURL(f);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      let body: BodyInit;
      let headers: Record<string, string> = {};
      if (screenshot) {
        const fd = new FormData();
        fd.append("type", type);
        fd.append("content", content);
        fd.append("screenshot", screenshot);
        body = fd;
      } else {
        headers["Content-Type"] = "application/json";
        body = JSON.stringify({ type, content });
      }
      const resp = await fetch("/api/feedback", { method: "POST", headers, body, credentials: "include" });
      const data = await resp.json();
      if (data.success) {
        setResult({ kind: "success", node: "Submitted! Item created in prnt." });
      } else {
        throw new Error(data.error || "Failed to submit");
      }
    } catch (err) {
      setResult({ kind: "error", node: `Error: ${(err as Error).message}` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="functions-panel active">
      <h3>{title}</h3>
      <p className="panel-description">
        {type === "bug"
          ? "Report a bug. Creates a P1 task in prnt tagged #tpfl #bug."
          : "Request a feature. Creates a P1 task in prnt tagged #tpfl #fr."}
      </p>
      <form onSubmit={submit}>
        <div className="form-group">
          <label>Description</label>
          <textarea
            rows={5}
            placeholder="what's goin on?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onPaste={(e) => {
              const items = e.clipboardData?.items;
              if (!items) return;
              for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf("image") !== -1) {
                  e.preventDefault();
                  const blob = items[i].getAsFile();
                  if (blob) setFile(blob);
                  break;
                }
              }
            }}
            required
          />
        </div>
        <div className="form-group">
          <label>Screenshot (optional)</label>
          {!preview ? (
            <div className="screenshot-zone" onClick={() => fileRef.current?.click()}>
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                ref={fileRef}
                onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
              />
              <span className="screenshot-zone-text">Click to attach or paste a screenshot</span>
            </div>
          ) : (
            <div className="screenshot-preview" style={{ display: "inline-block" }}>
              <img src={preview} alt="" />
              <button
                type="button"
                className="screenshot-remove"
                onClick={() => {
                  setScreenshot(null);
                  setPreview("");
                  if (fileRef.current) fileRef.current.value = "";
                }}
              >
                &times;
              </button>
            </div>
          )}
        </div>
        <ResultBox result={result} />
        <div className="form-actions">
          <button type="button" className="cancel-btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="submit-btn" disabled={busy}>
            {busy ? "Submitting..." : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
